// src/data/mediaItems.js - Record 613 (Bob 10/8). RULING 157 ("agreed", data\YCP_RULINGS_2026-09-17.md "## 157."):
// "MEDIA: A TRACKER FOR ALL CLASSES; STORAGE FOR CARRIER-PRODUCED MEDIA ONLY; ORGANISATION AND MEMBER RECORDINGS ARE
// POINTER-ONLY IN V1 (WRITE-TIME REJECTION)". Reading: "`storage.rules` must reject an upload whose class is not
// carrier-produced; the tracker row carries the pointer."
//
// The storage half has been in force since 2026-09-17 (storage.rules recordingAllowed(); the claim-upload callable's
// validateClaimUpload). THIS is the tracker half: one row per recording on a claim or a discipline case -
//   claims/{claimId}/mediaItems/{id}  and  disciplineCases/{caseId}/mediaItems/{id}
//   { class: carrier|organization|member, kind: audio|video|other, description, recordedOn (yyyy-mm-dd or ''),
//     recordedBy, heldBy, where (location or pointer text - a URL is allowed), storagePath (carrier class ONLY: an
//     uploaded file of THIS claim or case), createdBy (the writer's uid), createdAt (server time) }
//
// PURE. No firebase. The page (src\components\MediaTracker.js) writes what validateMediaItem() returns; firestore.rules
// (mediaItemShape) refuses anything else. Proved by scripts\test-media-tracker.mjs; the rules by
// scripts\test-firestore-rules.mjs (R613).

import { TEST_SCOPE } from './disciplineLadder';   // ruling 312 phase 1 (e): one definition of the test territory
// The committee a TEST| key belongs to ('TEST|GO-342|SANDBOX' -> 'GO-342'), as src\data\testTerritory.js reads it.
const testScopeCommitteeOf = (key) => { const m = /^TEST\|([^|]+)\|SANDBOX$/.exec(String(key || '')); return m ? m[1] : ''; };
export const TRACKER_TITLE = 'Recordings tracker';
export const MEDIA_CLASSES = Object.freeze(['carrier', 'organization', 'member']);
export const MEDIA_KINDS = Object.freeze(['audio', 'video', 'other']);
export const CLASS_LABELS = Object.freeze({ carrier: 'Carrier-produced', organization: 'Made by the organization', member: 'Made by a member' });
export const KIND_LABELS = Object.freeze({ audio: 'Audio', video: 'Video', other: 'Other media' });
export const LIMITS = Object.freeze({ description: 2000, recordedBy: 200, heldBy: 200, where: 2000 });
// Every key a row may carry - the same list as firestore.rules mediaItemShape() keys().hasOnly().
export const ITEM_KEYS = Object.freeze(['class', 'kind', 'description', 'recordedOn', 'recordedBy', 'heldBy', 'where',
  'storagePath', 'createdBy', 'createdAt']);

// The refusal a person reads when a recording that is not carrier-produced is offered for upload. It names the
// tracker by the title the tracker carries on the page (TRACKER_TITLE), and the tracker is on both pages that upload
// (ClaimDetail, the discipline case view) - scripts\test-media-tracker.mjs holds the two to each other. Ruling 168: a
// member reads this, so no ruling number is printed in it.
export const RECORDING_REFUSAL = 'Not uploaded. A recording is stored only when the carrier produced it. A recording made '
  + 'by the organization or by a member is not stored: list it in the ' + TRACKER_TITLE + ' on this page - who made it, '
  + 'who holds it and where it is.';
export const RECORDING_CONFIRM = 'Was this recording produced by the CARRIER (for example the carrier\'s own recording of '
  + 'the investigation)?\n\nOK - yes, carrier-produced: it is stored with the case.\nCancel - no: it is not stored; list it '
  + 'in the ' + TRACKER_TITLE + ' instead.';

const s = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim());

// The folder an uploaded file of this parent lives in - the same paths the upload code writes
// (claimDocumentUpload.claimDocumentObjectPath; DisciplineCases.js `discipline/${caseId}/...`).
export function storagePrefix(parentKind, parentId) {
  const id = s(parentId);
  return parentKind === 'discipline' ? 'discipline/' + id + '/' : 'claims/' + id + '/documents/';
}

function validDate(t) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return false;
  const d = new Date(t + 'T12:00:00Z');
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === t;
}

// Validate a form's entries. Returns { ok, errors[], item } - item is the row WITHOUT createdBy/createdAt (the page adds
// the signed-in uid and the server time, which is all the rules accept for them).
export function validateMediaItem(input, ctx) {
  const x = input || {};
  const c = ctx || {};
  const errors = [];
  const item = {
    class: s(x.class),
    kind: s(x.kind),
    description: s(x.description),
    recordedOn: s(x.recordedOn),
    recordedBy: s(x.recordedBy),
    heldBy: s(x.heldBy),
    where: s(x.where),
    storagePath: s(x.storagePath),
  };
  if (MEDIA_CLASSES.indexOf(item.class) === -1) errors.push('Say whose recording it is: carrier-produced, made by the organization, or made by a member.');
  if (MEDIA_KINDS.indexOf(item.kind) === -1) errors.push('Say what it is: audio, video, or other media.');
  if (!item.description) errors.push('Describe the recording (what it is of).');
  if (item.description.length > LIMITS.description) errors.push('The description is longer than ' + LIMITS.description + ' characters.');
  if (item.recordedOn && !validDate(item.recordedOn)) errors.push('The date recorded must be a real date (yyyy-mm-dd), or left blank if it is not known.');
  if (item.recordedBy.length > LIMITS.recordedBy) errors.push('"Recorded by" is longer than ' + LIMITS.recordedBy + ' characters.');
  if (item.heldBy.length > LIMITS.heldBy) errors.push('"Held by" is longer than ' + LIMITS.heldBy + ' characters.');
  if (item.where.length > LIMITS.where) errors.push('"Where it is" is longer than ' + LIMITS.where + ' characters.');
  if (item.storagePath) {
    if (item.class !== 'carrier') {
      errors.push('A stored file is linked only to a carrier-produced recording. A recording made by the organization or a member is not stored - say who holds it and where it is.');
    } else {
      const pre = storagePrefix(c.parentKind, c.parentId);
      const rest = item.storagePath.slice(pre.length);
      if (!s(c.parentId) || item.storagePath.indexOf(pre) !== 0 || !rest || rest.indexOf('/') !== -1) {
        errors.push('The linked file must be a file uploaded to THIS ' + (c.parentKind === 'discipline' ? 'case' : 'claim') + '.');
      }
    }
  }
  if (!item.heldBy && !item.where && !item.storagePath) errors.push('Say who holds the recording or where it is (or, for a carrier recording, link the uploaded file).');
  return { ok: errors.length === 0, errors, item: errors.length ? null : item };
}

// The uploaded files of this parent that a carrier row may link: its own documents, recordings first.
export function carrierFileChoices(parentKind, parentId, documents) {
  const pre = storagePrefix(parentKind, parentId);
  const list = (Array.isArray(documents) ? documents : []).filter(d => d && typeof d.storagePath === 'string'
    && d.storagePath.indexOf(pre) === 0 && d.storagePath.slice(pre.length) && d.storagePath.slice(pre.length).indexOf('/') === -1);
  const isRec = (d) => /^(audio|video)\//.test(String(d.fileType || '')) || /recording/i.test(String(d.type || ''))
    || /\.(mp3|m4a|wav|mp4|mov)$/i.test(String(d.name || ''));
  return list.filter(isRec).concat(list.filter(d => !isRec(d)))
    .map(d => ({ storagePath: d.storagePath, label: (d.name || d.storagePath) + (d.type ? ' (' + d.type + ')' : '') }));
}

// May this person ADD a row? The page's mirror of the rules (firestore.rules claims/.../mediaItems mParty() and
// disciplineCases/.../mediaItems mUploader()) - the parties who may upload to the parent. The rules decide; this only
// decides whether the form is offered. user: { uid, role, isAdmin, committee, scopes[], email }.
export function mayAddMediaItem(user, parentKind, parent) {
  const u = user || {};
  const p = parent || {};
  if (!u.uid) return false;
  if (p.createdBy && p.createdBy === u.uid) return true;   // the author, draft or not (ruling 56: a draft is his)
  if (p.status === 'draft') return false;
  if (u.isAdmin || u.role === 'admin') return true;
  const scopes = Array.isArray(u.scopes) ? u.scopes : [];
  if (parentKind === 'discipline') {
    if (u.role === 'local_chair' && p.scopeKey && scopes.indexOf(p.scopeKey) !== -1) return true;
    if (u.role === 'general_chair' && u.committee && u.committee === p.generalCommittee) return true;
    // Record 613, F22: a tester on an EXAMPLE in the test territory (src\data\testTerritory.js; rules testerSandbox()).
    // Ruling 312 phase 1 (e): the territory is TEST_SCOPE (src\data\disciplineLadder.js), its one definition in src.
    if (u.role === 'tester' && p.isExample === true && p.scopeKey === TEST_SCOPE
        && scopes.indexOf(TEST_SCOPE) !== -1 && u.committee === testScopeCommitteeOf(TEST_SCOPE)) return true;
    return false;
  }
  if (u.role === 'local_chair' && p.scopeKey && scopes.indexOf(p.scopeKey) !== -1) return true;
  if (u.role === 'general_chair' && u.committee && u.committee === p.committee) return true;
  if (u.role === 'member') {
    if (p.claimantUid && p.claimantUid === u.uid) return true;
    if (p.claimantEmail && u.email && p.claimantEmail === u.email) return true;
  }
  return false;
}

// Newest first, by the server time the row was written (a row still pending its server time sorts first).
export function sortItems(rows) {
  const ms = (r) => (r && r.createdAt && typeof r.createdAt.toMillis === 'function' ? r.createdAt.toMillis()
    : r && r.createdAt instanceof Date ? r.createdAt.getTime() : Number.MAX_SAFE_INTEGER);
  return (Array.isArray(rows) ? rows.slice() : []).sort((a, b) => ms(b) - ms(a));
}

// A discipline upload of a RECORDING (audio/video): stored only when the uploader says the carrier produced it - then
// the file carries customMetadata.mediaOrigin = 'carrier_produced', which storage.rules recordingAllowed() requires.
// Otherwise nothing is uploaded and he is pointed to the tracker. A document passes through unchanged.
//   file: { type, name }   tag: disciplineObjectTag()'s { customMetadata } (or undefined)   confirmFn: () => boolean
// Returns { tag, refused } - refused is the sentence to show, or '' when the upload may go ahead with `tag`.
export function isRecordingFile(file) {
  const f = file || {};
  return /^(audio|video)\//.test(String(f.type || '')) || /\.(mp3|m4a|wav|aac|ogg|mp4|mov|m4v|avi|wmv|webm)$/i.test(String(f.name || ''));
}
export function recordingUploadTag(file, tag, confirmFn) {
  if (!isRecordingFile(file)) return { tag, refused: '' };
  const yes = typeof confirmFn === 'function' ? !!confirmFn(RECORDING_CONFIRM) : false;
  if (!yes) return { tag: undefined, refused: RECORDING_REFUSAL };
  const md = Object.assign({}, (tag && tag.customMetadata) || {}, { mediaOrigin: 'carrier_produced' });
  return { tag: Object.assign({}, tag || {}, { customMetadata: md }), refused: '' };
}
